// ============================================================================
// 입력 — Pointer Events 단일 경로, 삼켜진 좌표 복원, 이중 속도 추정, 관성 속도.
// 좌표는 world unit (z=0 평면). 시각은 performance.now() 기준 ms.
// ============================================================================
import { P } from './params';

export interface Sample {
  x: number;
  y: number;
  t: number; // ms (performance.now timeline)
  coalesced: boolean; // getCoalescedEvents 로 복원된 샘플인지
}

export interface Vec2 {
  x: number;
  y: number;
}

export type ToWorld = (clientX: number, clientY: number) => Vec2;

export class PointerInput {
  down = false;
  pointerId: number | null = null;
  /** 현재 손가락 위치 (물리 스텝에서 소비된 샘플 기준) */
  pos: Vec2 = { x: 0, y: 0 };
  /** 이벤트 시각에 직접 갱신되는 위치 (렌더 1프레임 응답용) */
  rawPos: Vec2 = { x: 0, y: 0 };
  downPos: Vec2 = { x: 0, y: 0 };
  downT = 0;
  lastSampleT = 0;
  /** 빠른 속도 추정 (소리/진동) */
  vFast: Vec2 = { x: 0, y: 0 };
  /** 느린 속도 추정 (시각 변형) */
  vSlow: Vec2 = { x: 0, y: 0 };
  /** 손 뗀 순간 관성 속도 (마지막 몇 샘플만) */
  releaseVel: Vec2 = { x: 0, y: 0 };
  releaseT = 0;

  /** 물리 스텝이 소비할 큐 */
  private queue: Sample[] = [];
  /** 최근 샘플 (관성 계산 + 진단 표시용) */
  history: Sample[] = [];
  private static HISTORY_MAX = 96;

  // 진단 통계
  statSamplesThisFrame = 0;
  statCoalescedThisFrame = 0;
  statEventsThisFrame = 0;
  statSamplesPerSec = 0;
  private statWindow: number[] = [];

  // 콜백 (이벤트 시각에 즉시 호출 — 오디오 웜업, 첫 소리 등)
  onDown: ((p: Vec2, t: number) => void) | null = null;
  onUp: ((p: Vec2, t: number, releaseVel: Vec2) => void) | null = null;
  onFirstInteraction: (() => void) | null = null;
  private interacted = false;

  constructor(private el: HTMLElement, private toWorld: ToWorld) {
    el.addEventListener('pointerdown', this.handleDown, { passive: false });
    el.addEventListener('pointermove', this.handleMove, { passive: false });
    el.addEventListener('pointerup', this.handleUp, { passive: false });
    el.addEventListener('pointercancel', this.handleUp, { passive: false });
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private push(s: Sample) {
    this.queue.push(s);
    this.history.push(s);
    if (this.history.length > PointerInput.HISTORY_MAX) this.history.shift();
    this.statSamplesThisFrame++;
    if (s.coalesced) this.statCoalescedThisFrame++;
    this.statWindow.push(s.t);
  }

  private handleDown = (e: PointerEvent) => {
    if (this.down) return; // 첫 포인터만
    e.preventDefault();
    if (!this.interacted) {
      this.interacted = true;
      this.onFirstInteraction?.();
    }
    this.down = true;
    this.pointerId = e.pointerId;
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const w = this.toWorld(e.clientX, e.clientY);
    const t = e.timeStamp;
    this.rawPos = { ...w };
    this.pos = { ...w };
    this.downPos = { ...w };
    this.downT = t;
    this.lastSampleT = t;
    this.vFast = { x: 0, y: 0 };
    this.vSlow = { x: 0, y: 0 };
    this.history.length = 0;
    this.queue.length = 0;
    this.push({ x: w.x, y: w.y, t, coalesced: false });
    this.statEventsThisFrame++;
    this.onDown?.(w, t);
  };

  private handleMove = (e: PointerEvent) => {
    if (!this.down || e.pointerId !== this.pointerId) return;
    e.preventDefault();
    this.statEventsThisFrame++;
    const evs: PointerEvent[] =
      P.input.useCoalesced && typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    if (evs.length === 0) {
      const w = this.toWorld(e.clientX, e.clientY);
      this.rawPos = w;
      this.push({ x: w.x, y: w.y, t: e.timeStamp, coalesced: false });
      return;
    }
    let lastT = this.lastQueuedT();
    for (let i = 0; i < evs.length; i++) {
      const c = evs[i];
      const w = this.toWorld(c.clientX, c.clientY);
      // 일부 브라우저는 coalesced timeStamp 가 단조증가하지 않음 → 보정
      let t = c.timeStamp;
      if (!(t > lastT)) t = lastT + 0.01;
      lastT = t;
      this.push({ x: w.x, y: w.y, t, coalesced: i < evs.length - 1 });
      this.rawPos = w;
    }
  };

  private lastQueuedT(): number {
    const h = this.history;
    return h.length ? h[h.length - 1].t : this.lastSampleT;
  }

  private handleUp = (e: PointerEvent) => {
    if (!this.down || e.pointerId !== this.pointerId) return;
    e.preventDefault();
    this.down = false;
    this.pointerId = null;
    const t = e.timeStamp;
    const w = this.toWorld(e.clientX, e.clientY);
    // 마지막 몇 샘플로 관성 속도 계산 (평활값 사용 금지)
    this.releaseVel = this.computeReleaseVelocity(t);
    this.releaseT = t;
    this.onUp?.(w, t, this.releaseVel);
  };

  /** 마지막 releaseWindowMs 안의 샘플로 최소제곱 기울기. 오래 멈춰 있었으면 0. */
  private computeReleaseVelocity(tUp: number): Vec2 {
    const h = this.history;
    if (h.length < 2) return { x: 0, y: 0 };
    const last = h[h.length - 1];
    if (tUp - last.t > P.input.releaseStaleMs) return { x: 0, y: 0 };
    const t0 = last.t - P.input.releaseWindowMs;
    let pts = h.filter((s) => s.t >= t0);
    if (pts.length < 3) pts = h.slice(-3);
    if (pts.length < 2) return { x: 0, y: 0 };
    let mt = 0, mx = 0, my = 0;
    for (const s of pts) {
      mt += s.t;
      mx += s.x;
      my += s.y;
    }
    mt /= pts.length;
    mx /= pts.length;
    my /= pts.length;
    let sxx = 0, sxy = 0, syy = 0;
    for (const s of pts) {
      const dt = s.t - mt;
      sxx += dt * dt;
      sxy += dt * (s.x - mx);
      syy += dt * (s.y - my);
    }
    if (sxx < 1e-6) return { x: 0, y: 0 };
    // ms → s
    return { x: (sxy / sxx) * 1000, y: (syy / sxx) * 1000 };
  }

  /**
   * 물리 스텝에서 호출. untilT(ms) 까지의 샘플을 실제 시간 간격으로 소비하며 속도 추정을 갱신.
   * 샘플이 없으면 holdMs 후 정지로 간주해 속도 추정을 0으로 끌어내린다.
   */
  consume(untilT: number, dtStep: number): void {
    const q = this.queue;
    let n = 0;
    while (n < q.length && q[n].t <= untilT) n++;
    if (n > 0) {
      for (let i = 0; i < n; i++) {
        const s = q[i];
        const dt = Math.max(0.0005, (s.t - this.lastSampleT) / 1000);
        const vx = (s.x - this.pos.x) / dt;
        const vy = (s.y - this.pos.y) / dt;
        this.blend(vx, vy, dt);
        this.pos.x = s.x;
        this.pos.y = s.y;
        this.lastSampleT = s.t;
      }
      q.splice(0, n);
    } else if (this.down && untilT - this.lastSampleT > P.input.holdMs) {
      // 이벤트가 안 오면 손이 멈춘 것. 0 속도 샘플로 취급.
      this.blend(0, 0, dtStep);
    } else if (!this.down) {
      this.blend(0, 0, dtStep);
    }
  }

  private blend(vx: number, vy: number, dt: number) {
    const af = 1 - Math.exp(-dt / P.input.fastTau);
    const as = 1 - Math.exp(-dt / P.input.slowTau);
    this.vFast.x += (vx - this.vFast.x) * af;
    this.vFast.y += (vy - this.vFast.y) * af;
    this.vSlow.x += (vx - this.vSlow.x) * as;
    this.vSlow.y += (vy - this.vSlow.y) * as;
  }

  /** 렌더 프레임마다 호출 — 프레임 단위 통계 리셋 */
  endFrame(now: number): void {
    this.statSamplesThisFrame = 0;
    this.statCoalescedThisFrame = 0;
    this.statEventsThisFrame = 0;
    const cutoff = now - 1000;
    while (this.statWindow.length && this.statWindow[0] < cutoff) this.statWindow.shift();
    this.statSamplesPerSec = this.statWindow.length;
  }

  /** 아직 소비되지 않은 샘플 수 (진단) */
  get pending(): number {
    return this.queue.length;
  }
}
